import ky from "ky";
import { getStandardHeaders } from "../../../../apiUtils";
import { useQuery } from "react-query";
import Skeleton from "@mui/material/Skeleton";
import React, { useContext, useMemo } from "react";
import { Alert } from "@mui/material";
import {
  Area,
  AreaChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { SimpleTooltip } from "./SimpleTooltip";
import { formatInTimeZone } from "date-fns-tz";
import {
  useTimezone,
  useChartColorScheme,
  ChartColorScheme,
} from "../../../../utils/hooks";
import { ClusterContext } from "@/pages/Clusters/context";

type ClusterSizeChartProps = {
  clusterId: string;
};

interface ClusterSizeData {
  ts: number;
  [key: string]: number; // Allow any string key for the different states
}

const fetchSizes = async (clusterId: string): Promise<ClusterSizeData[]> => {
  const route = `/api/v2/clusters/account/*/id/${clusterId}/size`;
  const sizesRaw = await ky
    .get(route, {
      headers: getStandardHeaders(),
    })
    .json();
  return sizesRaw as ClusterSizeData[];
};

const prettyColorScheme = [
  ["error", "Error", "#FF0055"],
  ["pausing", "Pausing", "url(#pausingPattern)"],
  ["stopping", "Stopping", "#BABABA"],
  ["queued", "Queued", "rgba(180, 220, 255, 0.8)"],
  ["starting", "Starting", "rgba(90, 170, 160, 0.75)"],
  ["instance_running", "Instance Running", "rgba(70, 130, 180, 0.85)"],
  ["downloading", "Downloading", "rgba(50, 100, 190, 0.9)"],
  ["ready_on_demand", "Ready", "rgba(45, 95, 220, 0.95)"],
  ["ready_spot", "Ready (Spot)", "url(#spotPattern)"],
];

const highContrastColorScheme = [
  ["error", "Error", "#E90050"],
  ["pausing", "Pausing", "url(#pausingPattern)"],
  ["stopping", "Stopping", "#BABABA"],
  ["queued", "Queued", "#FDD10B"],
  ["starting", "Starting", "#16AB95"],
  ["instance_running", "Instance Running", "#1DE5FF"],
  ["downloading", "Downloading", "#317CFF"],
  ["ready_on_demand", "Ready", "#183CFD"],
  ["ready_spot", "Ready (Spot)", "url(#spotPattern)"],
];

// we want "error" to show at top (first) on chart, but at right (last) in legend
const legendOrder = [3, 4, 5, 6, 7, 8, 1, 2, 0];

const calculateTicks = (data: ClusterSizeData[] | undefined) => {
  if (!data || data.length === 0) return [];

  const minTime = Math.min(...data.map((d) => d.ts));
  const maxTime = Math.max(...data.map((d) => d.ts));
  const timeRange = maxTime - minTime;

  // Choose an appropriate interval based on the time range
  // For ranges under 2 minutes, show 15-second intervals
  // For ranges under 5 minutes, show 30-second intervals
  // For ranges under 15 minutes, show 1-minute intervals
  // For ranges under 30 minutes, show 2-minute intervals
  // For ranges under 1 hour, show 5-minute intervals
  // For ranges under 2 hours, show 10-minute intervals
  // For ranges over 2 hours, show 15-minute intervals
  let interval = 15 * 1000; // 15 seconds in ms
  if (timeRange > 2 * 60 * 60 * 1000)
    interval = 15 * 60 * 1000; // > 2 hours
  else if (timeRange > 60 * 60 * 1000)
    interval = 10 * 60 * 1000; // > 1 hour
  else if (timeRange > 30 * 60 * 1000)
    interval = 5 * 60 * 1000; // > 30 minutes
  else if (timeRange > 15 * 60 * 1000)
    interval = 2 * 60 * 1000; // > 15 minutes
  else if (timeRange > 5 * 60 * 1000)
    interval = 60 * 1000; // > 5 minutes
  else if (timeRange > 2 * 60 * 1000) interval = 30 * 1000; // > 2 minutes

  // Round down to nearest interval
  const start = Math.floor(minTime / interval) * interval;
  // Use exact end time
  const end = maxTime;

  const ticks = [];
  for (let t = start; t <= end; t += interval) {
    ticks.push(t);
  }
  // Always include the exact end time if it's not already included
  if (ticks[ticks.length - 1] !== end) {
    ticks.push(end);
  }
  return ticks;
};

export const ClusterSizeChart = ({
  clusterId,
}: ClusterSizeChartProps): React.ReactElement => {
  const [displayTz] = useTimezone();
  const [colorSchemePreference] = useChartColorScheme();
  const clusterSizeDomains =
    colorSchemePreference === ChartColorScheme.HIGH_CONTRAST
      ? highContrastColorScheme
      : prettyColorScheme;

  const { cluster } = useContext(ClusterContext);

  const { error, data, isSuccess } = useQuery(
    // small optimization, possible todo: stop fetching if the cluster is stopped
    ["fetchSizes", clusterId],
    () => fetchSizes(clusterId as string),
    {
      refetchInterval: () => (cluster?.finishedAt === undefined ? 5000 : false),
    },
  );
  const legendComponent = useMemo(
    () => (
      <Legend
        wrapperStyle={{
          paddingTop: "10px",
          fontSize: "14px",
        }}
        payload={legendOrder.map((key) => {
          const label = clusterSizeDomains[key][1];
          const fill = clusterSizeDomains[key][2];
          return {
            value: label,
            type: "square",
            color: fill,
            formatter: () => <span style={{ color: "#000000" }}>{label}</span>,
          };
        })}
      />
    ),
    [],
  );
  const areaCharts = useMemo(
    () =>
      clusterSizeDomains
        .slice()
        .reverse()
        .map(([domain, label, fill]) => (
          <Area
            activeDot={false}
            isAnimationActive={false}
            name={label}
            type="stepAfter"
            key={domain}
            dataKey={domain}
            fill={fill}
            fillOpacity={1}
            stroke={"rgba(255, 255, 255, 0.45)"}
            strokeWidth={1.25}
            stackId="0"
          />
        )),
    [],
  );

  if (error) {
    return <Alert severity="error">{"Error fetching cluster sizes."}</Alert>;
  }

  return (
    <>
      {!isSuccess && (
        <Skeleton animation="wave" variant="rectangular" height={250} />
      )}
      {isSuccess && (
        <ResponsiveContainer height={250} width="100%">
          <AreaChart data={data}>
            <defs>
              <pattern
                id="spotPattern"
                width="4"
                height="4"
                patternUnits="userSpaceOnUse"
                patternTransform="rotate(45)"
              >
                <rect width="4" height="4" fill="rgba(45, 95, 220, 0.95)" />
                <path
                  d="M1,1 L3,1 L3,3 L1,3 Z"
                  fill="rgba(85, 135, 255, 0.95)"
                />
              </pattern>
              <pattern
                id="pausingPattern"
                width="4"
                height="4"
                patternUnits="userSpaceOnUse"
                patternTransform="rotate(45)"
              >
                <rect width="3" height="3" fill="#BABABA" />
              </pattern>
            </defs>
            <XAxis
              dataKey="ts"
              type="number"
              scale="time"
              domain={["dataMin", "dataMax"]}
              tickFormatter={(v) => {
                if (!data || data.length === 0) return "";
                return formatInTimeZone(new Date(v), displayTz, "HH:mm:ss");
              }}
              minTickGap={80}
              ticks={calculateTicks(data)}
            />
            <YAxis
              type="number"
              domain={[0, "dataMax + 1"]}
              label={{ value: "VMs", angle: -90, dx: -20 }}
              allowDecimals={false}
            />
            {legendComponent}
            {areaCharts}
            <Tooltip
              content={
                <SimpleTooltip
                  labelFormatter={(v) =>
                    formatInTimeZone(
                      new Date(v),
                      displayTz,
                      "LLL do, HH:mm:ss O",
                    )
                  }
                  showTotal
                  filterZeros
                />
              }
              wrapperStyle={{
                outline: "none",
                zIndex: 3, // Making sure the task prefix tooltip doesn't cover the other tooltips
              }}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </>
  );
};
